import { Client } from "@app/types/api";
import { Tabs, theme } from "@app/ui";
import React, { useMemo, useState } from "react";

export type HeaderTab = {
    name: string;
    title: string;
    permission: number | null;
    dataTest: string;
    iconDef: string;
};

export type HeaderTabsProps = {
    activeTab: string;
    tabs: Record<string, HeaderTab>;
    client: Client;
    onChangeTab: (tab: string) => void;
};

export const HeaderTabs = ({ activeTab, onChangeTab, tabs, client }: HeaderTabsProps) => {
    const [activeKey, setActiveKey] = useState(activeTab);
    const [hoveredTab, setHoveredTab] = useState(activeTab);

    const onChange = (key: string) => {
        setActiveKey(key);
        onChangeTab(key);
    };

    const {
        token: { colorPrimary },
    } = theme.useToken();

    const items = useMemo(() => {
        return Object.values(tabs)
            .map(({ name, title, permission, iconDef }) => {
                const isVisible =
                    !permission || client.permissions.includes(permission);
                if (!isVisible) return null;
                const isActive = activeKey === name;
                const isHovered = hoveredTab === name;

                return {
                    key: name,
                    label: (
                        <div
                            onMouseEnter={() => setHoveredTab(name)}
                            onMouseLeave={() => setHoveredTab(null)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 12,
                            }}
                        >
                            <i className={iconDef} style={{
                                width: 16,
                                height: 16,
                                fontSize: 16,
                                color: isActive || isHovered ? colorPrimary : '#515252',
                            }} />
                            {title}
                        </div>
                    ),
                };
            })
            .filter(Boolean);
    }, [client.permissions, activeKey, hoveredTab, tabs]);

    return (
        <div data-test="settings-nav-tabs" style={{ width: '100%' }}>
            <Tabs activeKey={activeTab} onChange={onChange} items={items} />
        </div>
    );
};